import { useMemo, useState, useCallback } from 'react'

import { useBrowserService, useExperimentService, useUserService } from '@lib/hooks'
import { UrlBasedUsageTracker } from '@lib/utils/tool-usage'
import { useUpdateCountSubmit } from '@components/section/go-templates-tool/local-storage-state'
import { useFreeLimitToTrialExperiment } from '@lib/hooks/experiments/use-free-limit-to-trial-experiment'
import { useExitIntentModal } from '@lib/hooks/experiments/use-exit-intent-modal'
import { CapiWsProvider } from '@lib/services/api/cheetah-provider'
import { navHelper } from '@lib/services/navigation'
import { useShouldTriggerWall as useShouldTriggerGoTemplatesWall } from '@components/section/go-templates-tool/use-should-trigger-wall'
import { isEligibleGoogleOneTap } from '@components/google-one-tap/eligibility'
import GoogleOneTapDefault from '@components/google-one-tap/default'
import { FreeUserTrialModal } from '@components/ui-lib/modal/free-user-trial-modal'
import { ToolProvider } from './tool-context'
import { ToolForm } from './input/tool-form'
import { ToolOutput } from './output/tool-output'
import { isGoTemplatesToolType, ToolProps } from './types'
import { ToolErrorToast } from './components/tool-error-toast'
import { ToolTextLimitModal, ToolUsageModal, ToolExitIntentModal } from './components/tool-modals'
import { ToolFooterBanner } from './components/tool-footer-banner'

import styles from './tool.module.scss'
import { noop } from '@lib/utils/noop'

type ToolProvidersProps = {
  defaultProps: ToolProps
  children: React.ReactNode
  shouldTriggerGoTemplatesWall: boolean
  triggerUsageWall: () => void
  triggerTextLimitWall: () => void
  toolUsageTracker?: UrlBasedUsageTracker
  updateCountSubmit: () => void
  sampleText?: string
  toolInputName: string
  isEligibleForToolsGoogleOneTap: boolean
  setShowToolsGoogleOneTap: (show: boolean) => void
}

function ToolProviders(props: ToolProvidersProps) {
  const {
    defaultProps,
    children,
    shouldTriggerGoTemplatesWall,
    triggerUsageWall,
    triggerTextLimitWall,
    toolUsageTracker,
    updateCountSubmit,
    sampleText,
    toolInputName,
    isEligibleForToolsGoogleOneTap,
    setShowToolsGoogleOneTap,
  } = props

  // Order matters here. We need to wrap the ToolProvider in the CapiWsProvider,so that the cheetah state and form methods are available to the ToolProvider.
  // Also sending a noop function for onTriggerWall to CapiWsProvider (need to refactor).
  // We trigger it in two places right now which are 1) anonymous usage and no cheetah access and 2) prompts limit exceeded, which we handle already.
  return (
    <CapiWsProvider
      skip={shouldTriggerGoTemplatesWall || !isGoTemplatesToolType(defaultProps.toolType)}
      onTriggerWall={noop}
      usageTracker={toolUsageTracker}
      onCheetahActionSend={updateCountSubmit}
      sampleText={sampleText}
      toolInputName={toolInputName}
    >
      <ToolProvider
        isEligibleForToolsGoogleOneTap={isEligibleForToolsGoogleOneTap}
        setShowToolsGoogleOneTap={setShowToolsGoogleOneTap}
        toolUsageTracker={toolUsageTracker}
        triggerUsageWall={triggerUsageWall}
        triggerTextLimitWall={triggerTextLimitWall}
        {...defaultProps}
      >
        {children}
      </ToolProvider>
    </CapiWsProvider>
  )
}

export function Tool(props: ToolProps) {
  const { anonymousUsageLimit, sampleText, inputName } = props
  const { isAnonymous } = useUserService().currentUser
  const updateCountSubmit = useUpdateCountSubmit(isAnonymous)
  const { isChrome } = useBrowserService()
  const { isToolsRedesign2_2LoggedInEnabled } = useExperimentService()
  const isEligibleForToolsGoogleOneTap = isEligibleGoogleOneTap({
    isAnonymous,
    isChrome,
    isGoogleOneTapEnabled: true,
    isSSO: navHelper.currentQueryParams.is_sso === 'true',
  })
  const [showToolsGoogleOneTap, setShowToolsGoogleOneTap] = useState(false)

  const [isWallOpen, setIsWallOpen] = useState(false)
  const [isTextLimitWallOpen, setIsTextLimitWallOpen] = useState(false)

  const {
    usageTracker: freeTrialUsageTracker,
    showTrialModal,
    setShowTrialModal,
    isToolsFreeUseLimitExperiment,
  } = useFreeLimitToTrialExperiment()
  const isToolsRedesign2_2LoggedIn = isToolsRedesign2_2LoggedInEnabled()

  const { inExitIntentExperiment, isExitModalOpen, closeExitModal } = useExitIntentModal()

  const toolUsageTracker = useMemo(() => {
    if (isAnonymous && anonymousUsageLimit) {
      return new UrlBasedUsageTracker(anonymousUsageLimit)
    }
    if (isToolsRedesign2_2LoggedIn && isToolsFreeUseLimitExperiment) {
      return freeTrialUsageTracker
    }
    return undefined
  }, [
    isAnonymous,
    anonymousUsageLimit,
    isToolsRedesign2_2LoggedIn,
    isToolsFreeUseLimitExperiment,
    freeTrialUsageTracker,
  ])

  const shouldTriggerGoTemplatesWall = useShouldTriggerGoTemplatesWall()

  const triggerUsageWall = useCallback(() => {
    if (isToolsFreeUseLimitExperiment) {
      setShowTrialModal(true)
    } else {
      setIsWallOpen(true)
    }
  }, [isToolsFreeUseLimitExperiment, setShowTrialModal, setIsWallOpen])

  const triggerTextLimitWall = useCallback(() => {
    setIsTextLimitWallOpen(true)
  }, [setIsTextLimitWallOpen])

  return (
    <ToolProviders
      defaultProps={props}
      shouldTriggerGoTemplatesWall={shouldTriggerGoTemplatesWall}
      triggerUsageWall={triggerUsageWall}
      triggerTextLimitWall={triggerTextLimitWall}
      toolUsageTracker={toolUsageTracker}
      updateCountSubmit={updateCountSubmit}
      sampleText={sampleText}
      toolInputName={inputName || 'toolInput'}
      isEligibleForToolsGoogleOneTap={isEligibleForToolsGoogleOneTap}
      setShowToolsGoogleOneTap={setShowToolsGoogleOneTap}
    >
      {showToolsGoogleOneTap && <GoogleOneTapDefault />}
      <div className={styles.toolContainer}>
        <ToolForm />
        <ToolOutput />
        <ToolErrorToast />
      </div>
      <ToolFooterBanner />

      <ToolUsageModal isOpen={isWallOpen} onClose={() => setIsWallOpen(false)} />
      <ToolTextLimitModal isOpen={isTextLimitWallOpen} onClose={() => setIsTextLimitWallOpen(false)} />
      {inExitIntentExperiment && <ToolExitIntentModal isOpen={isExitModalOpen} onClose={closeExitModal} />}
      {isToolsFreeUseLimitExperiment && (
        <FreeUserTrialModal show={showTrialModal} close={() => setShowTrialModal(false)} />
      )}
    </ToolProviders>
  )
}
